import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import { Close, UploadFile } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import { ComplianceStatus, quoteLineType, supplierQuoteAiResponseType, supplierQuoteImportStateType } from '../../types'
import { formatCurrency, SupplierSearchAutocomplete } from '../../helper'
import { useCancellableRequest } from '../../Catalogs/useCancellableRequest'
import ScrmWarningDialog from './ScrmWarningDialog'
import './SupplierQuoteImport.css'
import './AiSupplierQuoteImportDialog.css'

const MAX_FILE_BYTES = 20 * 1024 * 1024
const READ_TIMEOUT_MS = 5 * 60 * 1000

const ACCEPTED_FILES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'text/html': ['.html', '.htm'],
  'application/vnd.ms-outlook': ['.msg'],
  'message/rfc822': ['.eml'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
}

type OptionGroupSummary = { name: string; lines: number; total: number }

function rejectionMessage(rejections: FileRejection[]): string {
  const code = rejections[0]?.errors[0]?.code
  if (code === 'file-too-large') return 'This file is larger than 20 MB.'
  if (code === 'file-invalid-type') {
    return 'Upload a PDF, Excel (.xlsx), CSV, text, web page (.html), email (.msg, .eml) or image (PNG, JPG, GIF, WebP) file.'
  }
  return 'Upload one file at a time.'
}

function readErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { message?: string } | undefined)?.message
    if (message) return message
    if (err.code === 'ECONNABORTED' || err.response?.status === 504) {
      return 'This quote took too long to read. Try again, or use the Supplier Quote Template for very large quotes.'
    }
  }
  return 'The quote could not be read. Try again.'
}

export default function AiSupplierQuoteImportDialog({
  open,
  onClose,
  quoteId,
}: {
  open: boolean
  onClose: () => void
  quoteId: string
}) {
  const navigate = useNavigate()
  const { start, cancel } = useCancellableRequest()
  const [file, setFile] = useState<File | null>(null)
  const [reading, setReading] = useState(false)
  const [result, setResult] = useState<supplierQuoteAiResponseType | null>(null)
  const [provider, setProvider] = useState<string | null>(null)
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingImportState, setPendingImportState] = useState<supplierQuoteImportStateType | null>(null)

  const optionGroups = useMemo<OptionGroupSummary[]>(() => {
    if (!result) return []
    const groupByLine = new Map(result.review.lines.map(line => [line.LineNumber, line.optionGroup]))
    const groups = new Map<string, OptionGroupSummary>()
    for (const lineItem of result.lineItems) {
      const name = groupByLine.get(lineItem.LineNumber)
      if (!name) continue
      const group = groups.get(name) ?? { name, lines: 0, total: 0 }
      group.lines += 1
      group.total += lineItem.ExtCost ?? 0
      groups.set(name, group)
    }
    return [...groups.values()]
  }, [result])

  const reset = () => {
    cancel()
    setFile(null)
    setReading(false)
    setResult(null)
    setProvider(null)
    setSelectedGroups([])
    setSubmitting(false)
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const {
    getRootProps,
    getInputProps,
    open: chooseFiles,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    maxSize: MAX_FILE_BYTES,
    accept: ACCEPTED_FILES,
    disabled: reading,
    onDropAccepted: accepted => {
      setFile(accepted[0])
      setError(null)
    },
    onDropRejected: rejections => setError(rejectionMessage(rejections)),
  })

  const handleRead = () => {
    if (!file) return
    const { signal, isCurrent } = start()
    setReading(true)
    setError(null)

    const formData = new FormData()
    formData.append('file', file)
    axios
      .post<supplierQuoteAiResponseType>(`/quote/${quoteId}/supplierQuoteAI`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: READ_TIMEOUT_MS,
        signal,
      })
      .then(res => {
        if (!isCurrent()) return
        setResult(res.data)
        setProvider(res.data.review.supplierMatched ? res.data.header.vendorName : null)
      })
      .catch(err => {
        if (axios.isCancel(err) || !isCurrent()) return
        console.error(err.response?.data.error ?? err)
        setError(readErrorMessage(err))
      })
      .finally(() => {
        if (isCurrent()) setReading(false)
      })
  }

  const toggleGroup = (name: string) =>
    setSelectedGroups(current =>
      current.includes(name) ? current.filter(group => group !== name) : [...current, name],
    )

  const handleContinue = async () => {
    if (!result || !provider) return
    const { signal, isCurrent } = start()
    setSubmitting(true)
    setError(null)

    const groupByLine = new Map(result.review.lines.map(line => [line.LineNumber, line.optionGroup]))
    const firstLineNumber = result.lineItems[0]?.LineNumber ?? 1
    const lineItems: quoteLineType[] = result.lineItems
      .filter(lineItem => {
        const group = groupByLine.get(lineItem.LineNumber)
        return !group || selectedGroups.includes(group)
      })
      .map((lineItem, index) => ({ ...lineItem, LineNumber: firstLineNumber + index, Provider: provider }))
    const filtered = lineItems.length !== result.lineItems.length

    try {
      const { data: scrmResults } = await axios.post<{ LineNumber: number; Scrm: ComplianceStatus }[]>(
        `/quote/${quoteId}/checkScrmCompliance`,
        { lineItems },
        { signal },
      )
      if (!isCurrent()) return
      const scrmByLine = new Map<number, ComplianceStatus>(scrmResults.map(r => [r.LineNumber, r.Scrm]))
      const lineItemsWithScrm = lineItems.map(item => ({ ...item, Scrm: scrmByLine.get(item.LineNumber) ?? null }))
      const linesTotal = lineItemsWithScrm.reduce((total, lineItem) => total + (lineItem.ExtCost ?? 0), 0)

      const state: supplierQuoteImportStateType = {
        quoteId,
        lineItems: lineItemsWithScrm,
        quoteNumber: result.header.vendorQuoteNumber ?? '',
        supplier: provider,
        totalAmount: filtered ? linesTotal : result.header.totalAmount ?? linesTotal,
        expirationDate: result.header.expirationDate ? dayjs(result.header.expirationDate).toDate() : null,
        file,
      }

      if (lineItemsWithScrm.some(item => item.Scrm === ComplianceStatus.NotCompliant)) {
        setSubmitting(false)
        setPendingImportState(state)
        return
      }
      navigate(`/quote/${quoteId}/validation`, { state })
    } catch (err) {
      if (axios.isCancel(err) || !isCurrent()) return
      console.error(err)
      setError('Could not check SCRM compliance. Try again.')
      setSubmitting(false)
    }
  }

  const thingsToCheck = useMemo(() => {
    if (!result) return []
    const partByLine = new Map(result.lineItems.map(lineItem => [lineItem.LineNumber, lineItem.MfrPartNumber]))
    const partsByWarning = new Map<string, string[]>()
    for (const line of result.review.lines) {
      if (line.optionGroup && !selectedGroups.includes(line.optionGroup)) continue
      const part = partByLine.get(line.LineNumber) ?? `Line ${line.LineNumber}`
      for (const warning of line.warnings) partsByWarning.set(warning, [...(partsByWarning.get(warning) ?? []), part])
    }
    const lineWarnings = [...partsByWarning].map(([warning, parts]) =>
      parts.length === 1 ? `${parts[0]}: ${warning}` : `${warning} (${parts.length} lines)`,
    )
    return [...result.review.warnings, ...lineWarnings]
  }, [result, selectedGroups])

  const needsGroup = optionGroups.length > 0 && selectedGroups.length === 0
  const aiSupplierName = result?.header.vendorName

  return (
    <Dialog open={open} onClose={reading ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle variant="h6">Import Quote with AI</DialogTitle>
      <DialogContent className="import-supplier-quote-dialog-content">
        {!result && (
          <>
            <Typography color="text.secondary">
              Upload the supplier's quote as a PDF, Excel, CSV, web page, email or image. The AI reads the supplier,
              quote number, expiration and every line, and you review it before anything is saved.
            </Typography>
            <div {...getRootProps()} className="import-supplier-quote-dropzone">
              <input {...getInputProps()} />
              <UploadFile color="disabled" />
              <Typography>Drop a Quote to Upload</Typography>
              <Typography color="text.secondary">or</Typography>
              <Button variant="contained" onClick={chooseFiles} disabled={reading}>
                Choose File
              </Button>
            </div>
            {file && (
              <div className="import-supplier-quote-file-card">
                <Box flex={1}>
                  <Typography variant="body2">{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                </Box>
                {!reading && (
                  <IconButton size="small" onClick={() => setFile(null)} aria-label="Remove file">
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </div>
            )}
            {reading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Reading your quote. This usually takes 10 to 60 seconds.
                </Typography>
                <LinearProgress />
              </Box>
            )}
          </>
        )}
        {result && (
          <>
            <Box className="ai-supplier-quote-summary">
              <Typography color="text.secondary">Supplier Quote #</Typography>
              <Typography>{result.header.vendorQuoteNumber ?? '-'}</Typography>
              <Typography color="text.secondary">Expiration Date</Typography>
              <Typography>
                {result.header.expirationDate ? dayjs(result.header.expirationDate).format('MM/DD/YYYY') : '-'}
              </Typography>
              <Typography color="text.secondary">Total Amount</Typography>
              <Typography>{formatCurrency(result.header.totalAmount, '-')}</Typography>
              <Typography color="text.secondary">Lines Found</Typography>
              <Typography>{result.lineItems.length}</Typography>
            </Box>
            <SupplierSearchAutocomplete value={provider} onChange={setProvider} label="Supplier Name" fullWidth />
            {!result.review.supplierMatched && aiSupplierName && (
              <Alert severity="info">
                The quote is from "{aiSupplierName}", which is not in the supplier list. Pick the matching supplier.
              </Alert>
            )}
            {optionGroups.length > 0 && (
              <Box>
                <Typography>This quote has options. Choose which to import:</Typography>
                {optionGroups.map(group => (
                  <FormControlLabel
                    key={group.name}
                    className="ai-supplier-quote-option"
                    control={
                      <Checkbox
                        checked={selectedGroups.includes(group.name)}
                        onChange={() => toggleGroup(group.name)}
                      />
                    }
                    label={`${group.name} (${group.lines} lines, ${formatCurrency(group.total)})`}
                  />
                ))}
              </Box>
            )}
            {thingsToCheck.length > 0 && (
              <Alert severity="warning">
                <AlertTitle>Things to check</AlertTitle>
                <Box component="ul" margin={0} paddingLeft={2}>
                  {thingsToCheck.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </Box>
              </Alert>
            )}
          </>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {result && (
          <Button variant="text" onClick={reset} disabled={submitting}>
            Choose Another File
          </Button>
        )}
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {result ? (
          <Button variant="contained" disabled={!provider || needsGroup} loading={submitting} onClick={handleContinue}>
            Continue
          </Button>
        ) : (
          <Button variant="contained" disabled={!file} loading={reading} onClick={handleRead}>
            Read Quote
          </Button>
        )}
      </DialogActions>
      <ScrmWarningDialog
        lineItems={pendingImportState?.lineItems ?? null}
        onClose={() => setPendingImportState(null)}
        onImport={() => {
          if (!pendingImportState) return
          navigate(`/quote/${quoteId}/validation`, { state: pendingImportState })
        }}
      />
    </Dialog>
  )
}
